import { ClipboardModule } from '@angular/cdk/clipboard';
import { CommonModule, DatePipe } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ModuleWithProviders, NgModule, SecurityContext } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter } from '@angular/material/core';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { EditorModule } from '@tinymce/tinymce-angular';
import { ImageCropperComponent } from 'ngx-image-cropper';

import { MySafeHtml } from 'src/app/admin/shared/pipes/safe-html.pipe';
import { CalendarComponent } from 'src/app/vacation/calendar/calendar.component';
import { CalendarDialogComponent } from '../vacation/calendar/calendar-dialog/calendar-dialog.component';
import { CalendarHeaderComponent } from '../vacation/calendar/calendar-header/calendar-header.component';
import { AutocompletePersonComponent } from './components/autocomplete-person/autocomplete-person.component';
import { CalendarDirective } from './components/calendar-directive/calendar.directive';
import { MarkdownModule, SANITIZE } from 'ngx-markdown';
import { EditorComponent } from './components/editor/editor.component';
import { MarkdownEditorComponent } from './components/markdown-editor/markdown-editor.component';
import { MockEditorComponent } from './components/editor/mock-editor.component';
import { MockMarkdownEditorComponent } from './components/markdown-editor/mock-markdown-editor.component';
import { EmployeeFiltersDialogComponent } from './components/employee-filters-dialog/employee-filters-dialog.component';
import { EmployeeTableComponent } from './components/employee-table/employee-table.component';
import { FileUploadButtonComponent } from './components/file-upload-button/file-upload-button.component';
import { ImageUploadBase64Component } from './components/image-upload-base64/image-upload-base64.component';
import { MultiSelectFieldComponent } from './components/multi-select-field/multi-select-field.component';
import { ReportErrorListComponent } from './components/report-error-list/report-error-list.component';
import { SpinnerOverlayComponent } from './components/spinner-overlay/spinner-overlay.component';
import { TableWrapperComponent } from './components/table-wrapper/table-wrapper.component';
import { TimeLogInputComponent } from './components/time-log-input/time-log-input.component';
import { TimeLogTableComponent } from './components/time-log-table/time-log-table.component';
import { TravelRequestsTableComponent } from './components/travel-requests-table/travel-requests-table.component';
import { AddDestinationFormComponent } from './components/travel-requests/add-destination-form/add-destination-form.component';
import { TravelRequestsComponent } from './components/travel-requests/travel-requests.component';
import { WeeklyNuggetComponent } from './components/weekly-nugget/weekly-nugget.component';
import { WithLoadingComponent } from './components/with-loading/with-loading.component';
import { CustomDateAdapter } from './custom.date.adapter';
import { CalendarAddProjectDialogComponent } from './dialogs/calendar-add-project-dialog/calendar-add-project-dialog.component';
import { CalendarCreateDialogComponent } from './dialogs/calendar-create-dialog/calendar-create-dialog.component';
import { DialogComponent } from './dialogs/dialog/dialog.component';
import { EditTravelRequestDialogComponent } from './dialogs/edit-travel-request-dialog/edit-travel-request-dialog.component';
import { ImageCropDialogComponent } from './dialogs/image-crop-dialog/image-crop-dialog.component';
import { ImageDialogComponent } from './dialogs/image-dialog/image-dialog.component';
import { ImageListDialogComponent } from './dialogs/image-list-dialog/image-list-dialog.component';
import { ReportFeedbackDialogComponent } from './dialogs/report-feedback-dialog/report-feedback-dialog.component';
import { TimeLogTableDialogComponent } from './dialogs/time-log-table-dialog/time-log-table-dialog.component';
import { WidgetDashboardDialogComponent } from './dialogs/widget-dashboard-dialog/widget-dashboard-dialog.component';
import { NuggetContentDirective } from './directives/nugget-content.directive';
import { IfHasPermissionDirective } from './directives/if-has-permission.directive';
import { ApiPrefixInterceptor } from './interceptors/ApiPrefixInterceptor';
import { TokenInterceptor } from './interceptors/TokenInterceptor';
import { MaterialModule } from './material.module';
import { Base64ToImagePipe } from './pipes/base64-to-image.pipe';
import { FilterPipe } from './pipes/filter.pipe';
import { FormatBytesPipe } from './pipes/format-bytes.pipe';
import { HumanizeDatePipe } from './pipes/humanize-date.pipe';
import { RestOfListPipe } from './pipes/rest-of-list.pipe';
import { SecurePipe } from './pipes/secure.pipe';
import { WithLoadingPipe } from './pipes/with-loading.pipe';
import { HasPermissionPipe } from './pipes/has-permission.pipe';
import { ServiceModule } from './services/services.module';
import { AddPeopleDialogComponent } from './dialogs/add-people-dialog/add-people-dialog.component';
import { EmployeeCardComponent } from './components/employee-card/employee-card.component';
import { RadioGroupFormFieldComponent } from './components/radio-group-form-field/radio-group-form-field.component';
import { WeeklyNuggetSummaryComponent } from './components/weekly-nugget-summary/weekly-nugget-summary.component';
import { VacationTypeToIconPipe } from './pipes/vacation-type-to-icon.pipe';
@NgModule({
  imports: [
    ClipboardModule,
    CommonModule,
    EditorComponent,
    EditorModule,
    FormatBytesPipe,
    FormsModule,
    ImageCropperComponent,
    ImageUploadBase64Component,
    MarkdownEditorComponent,
    MarkdownModule.forRoot({ sanitize: { provide: SANITIZE, useValue: SecurityContext.HTML } }),
    MaterialModule,
    HasPermissionPipe,
    IfHasPermissionDirective,
    ReactiveFormsModule,
    RouterModule,
    ServiceModule.forRoot(),
    TranslateModule,
    WithLoadingPipe,
    AutocompletePersonComponent,
    EmployeeCardComponent,
    RadioGroupFormFieldComponent,
    VacationTypeToIconPipe,
    WeeklyNuggetSummaryComponent,
    MySafeHtml,
    Base64ToImagePipe,
    SpinnerOverlayComponent,
  ],
  declarations: [
    AddDestinationFormComponent,
    AddPeopleDialogComponent,
    CalendarAddProjectDialogComponent,
    CalendarComponent,
    CalendarCreateDialogComponent,
    CalendarDirective,
    CalendarDialogComponent,
    CalendarHeaderComponent,
    DialogComponent,
    EditTravelRequestDialogComponent,
    EmployeeFiltersDialogComponent,
    EmployeeTableComponent,
    FileUploadButtonComponent,
    FilterPipe,
    HumanizeDatePipe,
    ImageCropDialogComponent,
    ImageDialogComponent,
    ImageListDialogComponent,
    MockEditorComponent,
    MockMarkdownEditorComponent,
    MultiSelectFieldComponent,
    NuggetContentDirective,
    ReportErrorListComponent,
    ReportFeedbackDialogComponent,
    RestOfListPipe,
    SecurePipe,
    TableWrapperComponent,
    TimeLogInputComponent,
    TimeLogTableComponent,
    TimeLogTableDialogComponent,
    TravelRequestsComponent,
    TravelRequestsTableComponent,
    WeeklyNuggetComponent,
    WidgetDashboardDialogComponent,
    WithLoadingComponent,
  ],
  exports: [
    AddDestinationFormComponent,
    AddPeopleDialogComponent,
    AutocompletePersonComponent,
    Base64ToImagePipe,
    CalendarAddProjectDialogComponent,
    CalendarComponent,
    CalendarCreateDialogComponent,
    CalendarDialogComponent,
    CalendarDirective,
    CalendarHeaderComponent,
    EditorComponent,
    EditTravelRequestDialogComponent,
    EmployeeFiltersDialogComponent,
    EmployeeTableComponent,
    FileUploadButtonComponent,
    FilterPipe,
    FormatBytesPipe,
    HumanizeDatePipe,
    ImageCropDialogComponent,
    ImageListDialogComponent,
    ImageUploadBase64Component,
    ImageCropperComponent,
    MarkdownEditorComponent,
    MarkdownModule,
    MultiSelectFieldComponent,
    MySafeHtml,
    NuggetContentDirective,
    HasPermissionPipe,
    IfHasPermissionDirective,
    RestOfListPipe,
    SecurePipe,
    SpinnerOverlayComponent,
    TableWrapperComponent,
    TimeLogInputComponent,
    TimeLogTableComponent,
    TravelRequestsComponent,
    TravelRequestsTableComponent,
    WeeklyNuggetComponent,
    WeeklyNuggetSummaryComponent,
    WithLoadingComponent,
  ],
})
export class SharedModule {
  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        DatePipe,
        { provide: DateAdapter, useClass: CustomDateAdapter },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: ApiPrefixInterceptor,
          multi: true,
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: TokenInterceptor,
          multi: true,
        },
        { provide: DateAdapter, useClass: CustomDateAdapter },
      ],
    };
  }
}
